/**
 * `useFunnelState` — thin React glue over `@nextday/funnel-engine`.
 *
 * Brand-agnostic: the hook only consumes a `FunnelConfig` (resolved at
 * runtime for whichever brand the request belongs to) plus an optional
 * `chooseNextStep` adapter (typically built via
 * `chooseNextStepFromDecision` from `@nextday/survey-kit/branching`).
 *
 * The engine's pure-core state machine stays the source of truth; this
 * hook just exposes synchronous React-friendly handlers that mirror the
 * engine's `advance` / `goBack` / `goTo` / `restart`.
 */
import {
  advance,
  createFunnelState,
  getCurrentStep,
  goBack as engineGoBack,
  goTo as engineGoTo,
  restart as engineRestart,
  type Answer,
  type ChooseNextStep,
  type FunnelConfig,
  type FunnelState,
  type Step,
} from "@nextday/funnel-engine";
import { useCallback, useMemo, useState } from "react";

export type UseFunnelStateOptions = {
  /**
   * Optional initial state — used to resume a saved draft from localStorage
   * or D1. Falls back to `createFunnelState(config)` when omitted.
   */
  initialState?: FunnelState;
};

export type UseFunnelStateApi = {
  state: FunnelState;
  /** Current step (or `null` once the funnel has completed). */
  currentStep: Step | null;
  /**
   * Advance to the next step. Optionally records an answer for the current
   * step. Pass a `chooseNextStep` to override the linear walk — typically
   * constructed via `chooseNextStepFromDecision(await resolveBranching(...))`.
   */
  advance: (options?: {
    answer?: { value: unknown; answeredAt?: number };
    chooseNextStep?: ChooseNextStep;
  }) => void;
  goBack: () => void;
  goTo: (stepId: string, options?: { returnTo?: string }) => void;
  restart: () => void;
  recordedAnswers: ReadonlyArray<Answer>;
};

/**
 * Hook contract: callers either supply `initialState` (resume) or let the
 * hook seed from `config`. The hook never persists state itself — that's
 * the orchestrator's job (e.g. via `useFunnelDraft` for localStorage +
 * `apps/funnel-api` for D1 writes).
 */
export const useFunnelState = (
  config: FunnelConfig,
  { initialState }: UseFunnelStateOptions = {},
): UseFunnelStateApi => {
  const [state, setState] = useState<FunnelState>(() => initialState ?? createFunnelState(config));

  const advanceCb = useCallback(
    (options: Parameters<UseFunnelStateApi["advance"]>[0] = {}) => {
      setState((prev) => advance(prev, config, options));
    },
    [config],
  );

  const goBackCb = useCallback(() => {
    setState((prev) => engineGoBack(prev, config));
  }, [config]);

  const goToCb = useCallback(
    (stepId: string, options?: { returnTo?: string }) => {
      setState((prev) => engineGoTo(prev, config, stepId, options));
    },
    [config],
  );

  const restartCb = useCallback(() => {
    setState(engineRestart(config));
  }, [config]);

  const currentStep = useMemo(() => getCurrentStep(state, config), [state, config]);

  return {
    state,
    currentStep,
    advance: advanceCb,
    goBack: goBackCb,
    goTo: goToCb,
    restart: restartCb,
    recordedAnswers: state.answers,
  };
};
